import React, { useEffect, useMemo, useState } from 'react';
import {
  ExternalLink,
  GitBranch,
  Network,
  Terminal,
  FileText,
  TreePine,
  Brain,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';

type SubTab = 'brain' | 'tree' | 'report' | 'cli';

const BRAIN_URL = '/graphify/GRAPH_BRAIN.html';
const TREE_URL = '/graphify/GRAPH_TREE.html';
const REPORT_URL = '/graphify/GRAPH_REPORT.md';

function wikiLinkToText(line: string): string {
  return line.replace(/\[\[([^|\]]+)\|([^\]]+)\]\]/g, '$2').replace(/\[\[([^\]]+)\]\]/g, '$1');
}

/** Leichtgewichtiges Markdown-Rendering für GRAPH_REPORT.md (keine Extra-Dependency). */
function renderReportMarkdown(md: string): React.ReactNode[] {
  const lines = md.split('\n');
  const nodes: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const raw = lines[i];
    const line = wikiLinkToText(raw);

    if (line.startsWith('```')) {
      const lang = line.slice(3).trim();
      const buf: string[] = [];
      i += 1;
      while (i < lines.length && !lines[i].startsWith('```')) {
        buf.push(lines[i]);
        i += 1;
      }
      nodes.push(
        <pre
          key={key++}
          className="my-3 overflow-x-auto rounded-lg border border-slate-700 bg-slate-900/80 p-4 text-xs text-emerald-300/90 font-mono"
        >
          {lang ? <span className="block text-slate-500 mb-2">{lang}</span> : null}
          {buf.join('\n')}
        </pre>
      );
      i += 1;
      continue;
    }

    if (line.startsWith('### ')) {
      nodes.push(
        <h3 key={key++} className="mt-6 mb-2 text-base font-semibold text-white">
          {line.slice(4)}
        </h3>
      );
    } else if (line.startsWith('## ')) {
      nodes.push(
        <h2 key={key++} className="mt-8 mb-3 text-lg font-bold text-blue-300 border-b border-slate-800 pb-2">
          {line.slice(3)}
        </h2>
      );
    } else if (line.startsWith('# ')) {
      nodes.push(
        <h1 key={key++} className="mb-4 text-2xl font-bold text-white">
          {line.slice(2)}
        </h1>
      );
    } else if (/^[-*] /.test(line)) {
      nodes.push(
        <li key={key++} className="ml-5 list-disc text-sm text-slate-300 leading-relaxed">
          {inlineFormat(line.slice(2))}
        </li>
      );
    } else if (line.trim() === '') {
      nodes.push(<div key={key++} className="h-2" />);
    } else {
      nodes.push(
        <p key={key++} className="text-sm text-slate-300 leading-relaxed">
          {inlineFormat(line)}
        </p>
      );
    }
    i += 1;
  }

  return nodes;
}

function inlineFormat(text: string): React.ReactNode {
  const parts = text.split(/(`[^`]+`)/g);
  return parts.map((part, idx) => {
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={idx} className="rounded bg-slate-800 px-1.5 py-0.5 text-xs text-cyan-300 font-mono">
          {part.slice(1, -1)}
        </code>
      );
    }
    const boldParts = part.split(/(\*\*[^*]+\*\*)/g);
    return boldParts.map((bp, j) => {
      if (bp.startsWith('**') && bp.endsWith('**')) {
        return <strong key={`${idx}-${j}`} className="text-white font-semibold">{bp.slice(2, -2)}</strong>;
      }
      return <span key={`${idx}-${j}`}>{bp}</span>;
    });
  });
}

const CLI_COMMANDS = [
  { label: 'Graph aktualisieren (nach Code-Änderungen)', cmd: 'graphify update .' },
  { label: 'Frage an den Repo-Graphen', cmd: 'graphify query "Wie hängt Portal-Chat mit n8n zusammen?"' },
  { label: 'Pfad zwischen Konzepten', cmd: 'graphify path "DonnaWorkspace" "n8nService"' },
  { label: 'Konzept erklären', cmd: 'graphify explain "LEXCORE"' },
  { label: 'Interaktive Graph-UI (lokal)', cmd: 'python -m graphify.serve graphify-out/graph.json' },
];

const GraphifyDevMap: React.FC = () => {
  const [subTab, setSubTab] = useState<SubTab>('brain');
  const [reportMd, setReportMd] = useState<string | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [brainAvailable, setBrainAvailable] = useState<boolean | null>(null);
  const [treeAvailable, setTreeAvailable] = useState<boolean | null>(null);
  const [loadingReport, setLoadingReport] = useState(false);

  const loadReport = async () => {
    setLoadingReport(true);
    setReportError(null);
    try {
      const res = await fetch(REPORT_URL, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setReportMd(await res.text());
    } catch (e) {
      setReportError(e instanceof Error ? e.message : 'Laden fehlgeschlagen');
      setReportMd(null);
    } finally {
      setLoadingReport(false);
    }
  };

  useEffect(() => {
    fetch(BRAIN_URL, { method: 'HEAD' })
      .then((r) => setBrainAvailable(r.ok))
      .catch(() => setBrainAvailable(false));
    fetch(TREE_URL, { method: 'HEAD' })
      .then((r) => setTreeAvailable(r.ok))
      .catch(() => setTreeAvailable(false));
  }, []);

  useEffect(() => {
    if (subTab === 'report' && reportMd === null && !reportError && !loadingReport) {
      void loadReport();
    }
  }, [subTab, reportMd, reportError, loadingReport]);

  const reportNodes = useMemo(
    () => (reportMd ? renderReportMarkdown(reportMd) : null),
    [reportMd]
  );

  const subTabBtn = (id: SubTab, icon: React.ReactNode, label: string) => {
    const active = subTab === id;
    return (
      <button
        type="button"
        onClick={() => setSubTab(id)}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
          active
            ? 'bg-violet-600 text-white shadow-lg shadow-violet-900/40'
            : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
        }`}
      >
        {icon}
        {label}
      </button>
    );
  };

  return (
    <div className="flex flex-col h-full min-h-[calc(100vh-8rem)] animate-fade-in">
      <header className="flex-shrink-0 mb-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-violet-600/20 border border-violet-500/40 flex items-center justify-center">
                <Network className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white tracking-tight">Dev-Map (Graphify)</h1>
                <p className="text-xs text-slate-500 uppercase tracking-widest font-semibold">Repo-Orientierung · nicht LEXCORE</p>
              </div>
            </div>
            <p className="text-sm text-slate-400 max-w-3xl leading-relaxed">
              Interaktive Codebase-Karte aus <code className="text-cyan-400/90 font-mono text-xs">graphify-out/</code>.
              Für Rechtsnormen und Portal-<code className="font-mono text-xs">[LEX:…]</code> gilt weiterhin{' '}
              <code className="text-emerald-400/90 font-mono text-xs">data/lexcore/curated/</code> — nicht dieser Graph.
            </p>
          </div>
          <a
            href={BRAIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-700 text-slate-300 hover:text-white hover:border-violet-500/50 text-sm transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Brain in neuem Tab
          </a>
        </div>

        <div className="flex flex-wrap gap-2 mt-5 p-1 bg-slate-900/50 rounded-xl border border-slate-800 w-fit">
          {subTabBtn('brain', <Brain className="w-4 h-4" />, 'Brain (Obsidian)')}
          {subTabBtn('tree', <TreePine className="w-4 h-4" />, 'Baum')}
          {subTabBtn('report', <FileText className="w-4 h-4" />, 'Bericht')}
          {subTabBtn('cli', <Terminal className="w-4 h-4" />, 'CLI')}
        </div>
      </header>

      <div className="flex-1 min-h-0">
        {subTab === 'brain' && (
          <div className="h-full flex flex-col rounded-xl border border-slate-800 overflow-hidden bg-slate-900/30">
            {brainAvailable === false && (
              <div className="flex items-center gap-3 p-4 bg-amber-950/40 border-b border-amber-800/50 text-amber-200 text-sm">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>
                  <code className="font-mono">GRAPH_BRAIN.html</code> nicht unter{' '}
                  <code className="font-mono">/graphify/</code> — bitte{' '}
                  <code className="font-mono">npm run sync:static</code> oder{' '}
                  <code className="font-mono">graphify update .</code> im Repo-Root ausführen.
                </span>
              </div>
            )}
            <iframe
              title="Graphify Brain-Visualisierung"
              src={`${BRAIN_URL}?embed=1`}
              className="flex-1 w-full min-h-[60vh] bg-[#0a0e17]"
              sandbox="allow-scripts allow-same-origin"
            />
          </div>
        )}

        {subTab === 'tree' && (
          <div className="h-full flex flex-col rounded-xl border border-slate-800 overflow-hidden bg-slate-900/30">
            {treeAvailable === false && (
              <div className="flex items-center gap-3 p-4 bg-amber-950/40 border-b border-amber-800/50 text-amber-200 text-sm">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>
                  <code className="font-mono">GRAPH_TREE.html</code> nicht unter{' '}
                  <code className="font-mono">/graphify/</code> — bitte{' '}
                  <code className="font-mono">npm run sync:static</code> oder{' '}
                  <code className="font-mono">graphify update .</code> im Repo-Root ausführen.
                </span>
              </div>
            )}
            <iframe
              title="Graphify Baum-Visualisierung"
              src={TREE_URL}
              className="flex-1 w-full min-h-[60vh] bg-white"
              sandbox="allow-scripts allow-same-origin"
            />
          </div>
        )}

        {subTab === 'report' && (
          <div className="h-full overflow-y-auto rounded-xl border border-slate-800 bg-slate-900/40 p-6 md:p-8">
            {loadingReport && (
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Lade GRAPH_REPORT.md …
              </div>
            )}
            {reportError && (
              <div className="flex items-start gap-3 text-amber-200 text-sm">
                <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div>
                  <p>Bericht nicht geladen: {reportError}</p>
                  <button
                    type="button"
                    onClick={() => void loadReport()}
                    className="mt-2 text-violet-400 hover:text-violet-300 underline text-xs"
                  >
                    Erneut versuchen
                  </button>
                </div>
              </div>
            )}
            {reportNodes}
          </div>
        )}

        {subTab === 'cli' && (
          <div className="space-y-6 max-w-3xl">
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-6">
              <div className="flex items-center gap-2 mb-4">
                <GitBranch className="w-5 h-5 text-violet-400" />
                <h2 className="text-lg font-semibold text-white">graphify CLI (Repo-Root)</h2>
              </div>
              <p className="text-sm text-slate-400 mb-6 leading-relaxed">
                Im Terminal <code className="font-mono text-cyan-300">cd C:\ARKORBIT_CORE</code> — dann gezielt
                abfragen statt blind zu greppen. Nach Code-Änderungen:{' '}
                <code className="font-mono text-cyan-300">graphify update .</code> (AST-only, keine API-Kosten).
              </p>
              <ul className="space-y-4">
                {CLI_COMMANDS.map(({ label, cmd }) => (
                  <li key={cmd} className="rounded-lg border border-slate-700/80 bg-slate-950/60 p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">{label}</p>
                    <code className="block text-sm text-emerald-300/90 font-mono break-all">{cmd}</code>
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-xs text-slate-600">
              Cursor-Regel: <code className="font-mono">.cursor/rules/graphify.mdc</code> · Skill:{' '}
              <code className="font-mono">.claude/skills/graphify/</code>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GraphifyDevMap;
